/**
 * ToastOverlay Component
 *
 * Bridge between the notificationStore and Fluent UI's Toaster.
 * Uses Fluent UI Toaster for positioning, stacking, animations, and lifecycle,
 * but renders custom-styled toast content with left accent bar, icons,
 * and a dismiss button on every toast.
 *
 * Auto-dismiss: success/info 4s, warning 6s, error never auto-dismisses (requires manual dismiss).
 * Position: top-end (top-right).
 */

import { useEffect, useRef, useCallback, useId } from 'react';
import {
  Toaster,
  toasterClassNames,
  useToastController,
  Toast,
  ToastTrigger,
  Button,
  makeStyles,
  tokens,
  mergeClasses,
} from '@coreai/fluentui-react';
import {
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  WarningRegular,
  InfoRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import {
  useNotificationStore,
  type Notification,
  type NotificationType,
} from '@/stores/notificationStore';
import { getNotificationDisplayContent } from '@/utils/primitives';

const TIMEOUT_MS: Record<NotificationType, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: -1, // Errors require manual dismiss — never auto-dismiss
};

// The Toaster column and the card must agree: if the column is narrower than
// the card, the card overflows and gets visually clipped. Keep them derived
// from one value so they can't drift apart.
const TOAST_WIDTH = '356px';
const TOAST_RADIUS = '12px';

const useStyles = makeStyles({
  // Fluent's Toaster root is a hard-coded 292px (`makeResetStyles`), narrower
  // than our card — the card overflowed its column and got clipped, cutting
  // off the right corner radius and half the shadow.
  // The reset rule is injected into a later stylesheet than `makeStyles`, so a
  // plain class override loses the cascade at equal specificity. Nesting under
  // the public `fui-Toaster` class raises specificity enough to win.
  toaster: {
    [`&.${toasterClassNames.root}`]: {
      width: TOAST_WIDTH,
    },
  },

  // Reset default Toast styles (the "double background" fix)
  toastContainer: {
    // Fluent's Toast root is a grid (`auto 1fr auto`) for its own slot layout.
    // We render a single custom card, which would land in the content-sized
    // `auto` track and vary in width with the message. Block layout lets the
    // card resolve `width: 100%` against the full toaster column instead.
    display: 'block',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: 'none',
    padding: 0,
    width: 'auto', // fills the toaster column (block layout)
    maxWidth: '100%',
    // Match the inner card radius so the Fluent focus ring follows the card
    // silhouette instead of the default 4px radius.
    borderRadius: TOAST_RADIUS,
    // Speed up the enter/exit animations (Fluent default is ~400ms)
    animationDuration: tokens.durationNormal,
    '& [data-fui-toast]': {
      animationDuration: tokens.durationNormal,
    },
    ':hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
  },

  // Main Toast Container
  toast: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gap: tokens.spacingHorizontalM,
    alignItems: 'start',
    paddingBlock: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalL,
    borderRadius: TOAST_RADIUS,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16, // Theme-aware elevation (breaks in dark mode if hardcoded)
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    width: '100%',
    maxWidth: TOAST_WIDTH,
    boxSizing: 'border-box', // Keep padding/border inside the toaster column
    pointerEvents: 'auto',
    // Clip content to the card's rounded corners. The card owns the radius,
    // so this is the only layer that should hide overflow.
    overflow: 'hidden',
  },
  
  // Icon Styling
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    marginTop: tokens.spacingVerticalXXS, // Align with title text
    fontSize: tokens.fontSizeBase500,
  },
  
  // Icon Variants
  iconSuccess: { color: tokens.colorPaletteGreenForeground1 },
  iconError: { color: tokens.colorPaletteRedForeground1 },
  iconWarning: { color: tokens.colorPaletteYellowForeground1 },
  iconInfo: { color: tokens.colorBrandForeground1 },

  // Content Area
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalXXS, // Align with icon
    minWidth: 0, // Allow shrinking inside grid track so long messages wrap
  },
  title: {
    fontWeight: tokens.fontWeightSemibold, // Semibold
    fontSize: tokens.fontSizeBase300,
    lineHeight: '20px',
    color: tokens.colorNeutralForeground1,
    overflowWrap: 'anywhere',
  },
  message: {
    fontSize: '13px',
    lineHeight: '18px',
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'pre-wrap', // Preserve line breaks
    overflowWrap: 'anywhere', // Break inside long unbroken strings (e.g. JSON error bodies)
    wordBreak: 'break-word', // Fallback for browsers that don't fully support overflowWrap: anywhere
    // Cap the message area so the toast stays compact; full message is always
    // accessible in the notification panel (bell icon).
    maxHeight: '120px',
    overflow: 'hidden',
  },

  // Close Button (Ghost style, always visible but subtle)
  dismiss: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
  },

  // Container for optional action button + dismiss button
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },

  // Action link rendered below the message inside the content column
  actionButton: {
    color: tokens.colorBrandForeground1,
    minWidth: 'auto',
    padding: 0,
    height: 'auto',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXS,
    alignSelf: 'flex-start',
  },
});

const ICON_MAP: Record<NotificationType, React.ReactNode> = {
  success: <CheckmarkCircleRegular />,
  error: <ErrorCircleRegular />,
  warning: <WarningRegular />,
  info: <InfoRegular />,
};

const ICON_STYLE_KEY = {
  success: 'iconSuccess',
  error: 'iconError',
  warning: 'iconWarning',
  info: 'iconInfo',
} as const;

/** Bridge that watches notificationStore and dispatches styled Fluent UI toasts. */
const ToastBridge: React.FC<{ toasterId: string }> = ({ toasterId }) => {
  const styles = useStyles();
  const { dispatchToast } = useToastController(toasterId);
  const notifications = useNotificationStore((state) => state.notifications);
  const markAsToasted = useNotificationStore((state) => state.markAsToasted);
  const removeNotification = useNotificationStore((state) => state.removeNotification);
  const seenIdsRef = useRef<Set<string>>(new Set());

  // Dispatch a styled toast into the Fluent UI Toaster
  const dispatch = useCallback(
    (
      id: string,
      notification: Pick<Notification, 'type' | 'title' | 'message' | 'validationErrors'>,
      actionButton?: Notification['actionButton'],
      durationMs?: number,
    ) => {
      const { title, message } = getNotificationDisplayContent(notification);
      const { type } = notification;

      dispatchToast(
        <Toast className={styles.toastContainer}>
          <div className={styles.toast}>
            <span className={mergeClasses(styles.icon, styles[ICON_STYLE_KEY[type]])}>
              {ICON_MAP[type]}
            </span>
            
            <div className={styles.content}>
              {title && <span className={styles.title}>{title}</span>}
              {message && <span className={styles.message}>{message}</span>}
              {actionButton && (
                <ToastTrigger>
                  <Button
                    size="small"
                    appearance="transparent"
                    className={styles.actionButton}
                    onClick={() => {
                      removeNotification(id);
                      actionButton.onClick();
                    }}
                  >
                    {actionButton.label}
                  </Button>
                </ToastTrigger>
              )}
            </div>

            <div className={styles.actions}>
              <ToastTrigger>
                <button
                  type="button"
                  className={styles.dismiss}
                  aria-label="Dismiss"
                >
                  <DismissRegular fontSize={16} />
                </button>
              </ToastTrigger>
            </div>
          </div>
        </Toast>,
        {
          intent: type,
          timeout: durationMs ?? TIMEOUT_MS[type],
          toastId: id,
        },
      );
    },
    [dispatchToast, styles, removeNotification],
  );

  useEffect(() => {
    for (const n of notifications) {
      // seenIdsRef prevents duplicate dispatches within a render batch (synchronous guard),
      // while n.toasted persists the flag to sessionStorage so rehydrated notifications aren't re-shown.
      if (!n.toasted && !seenIdsRef.current.has(n.id)) {
        seenIdsRef.current.add(n.id);
        dispatch(n.id, n, n.actionButton, n.durationMs);
        markAsToasted(n.id);
      }
    }
  }, [notifications, dispatch, markAsToasted]);

  return null;
};

export const ToastOverlay: React.FC = () => {
  const toasterId = useId();
  const styles = useStyles();

  return (
    <>
      <Toaster
        toasterId={toasterId}
        className={styles.toaster}
        position="top-end"
        pauseOnHover
        pauseOnWindowBlur
        limit={5}
      />
      <ToastBridge toasterId={toasterId} />
    </>
  );
};
